/*
* This code is the intellectual property of Bulb Inc. and other software publishers;
* it may not be altered, copied or disclosed without prior written approval.
* Copyright 2021, Bulb Inc. For more information, please email contact@hellobulb.com.
*/
import {
  Component, EventEmitter, Input, OnInit, Output, ViewChild,
} from '@angular/core';
import { Subject, Observable } from 'rxjs';
import {
  debounceTime, distinctUntilChanged, switchMap, tap, filter, take, map,
} from 'rxjs/operators';
import { MultiSelect } from 'primeng/multiselect';

import { TranslocoService } from '@jsverse/transloco';
import { PageableUsers, SubjectViewModel } from '../share-panel.d';
import { ShareStateStore, SharePanelOverlays } from '../share-state.store';

type OptionGroup = {
  label: string,
  items: SubjectViewModel[]
};

type PageState = {
  isLast: boolean,
  page: number,
};

@Component({
  selector: 'bulb-async-dropdown',
  templateUrl: './async-dropdown.component.html',
  styleUrls: ['./async-dropdown.component.scss'],
  standalone: false,
})
export class AsyncDropdownComponent implements OnInit {
  @Input() initialGroupFetch!: () => Observable<any>;

  @Input() secondGroupFetch: (filterQuery: string, page: number) => Observable<PageableUsers>;

  @Input() excludeGroupFetchItems: SubjectViewModel[] = [];

  @Input() fieldId!: string;

  @Input() fieldLabel: string;

  @Output() selectedOptions: EventEmitter<SubjectViewModel[]> = new EventEmitter<SubjectViewModel[]>();

  @ViewChild('multiSelectComponent') multiSelectComponent: MultiSelect;

  public selectedItems: any[] = [];

  public filterValue: string = '';

  private filterTextChanged: Subject<string> = new Subject<string>();

  private filterTextObsvr: Observable<PageableUsers>;

  public loadingSearchResults: boolean = false;

  public groupedOptions: OptionGroup[] = [
    {
      label: 'sharePanel.select.recent',
      items: [],
    },
    {
      label: 'sharePanel.select.searchResults',
      items: [],
    },
  ];

  public pageState: PageState = {
    isLast: false,
    page: 0,
  };

  /**
   * Is true when there is a queued request waiting ot be sent.
   * When there is a queued request, we want to ignore all but the latest request made.
   */
  private queuedRequest: boolean = false;

  public testClass: string = 'test-1';

  constructor(
    private modalState: ShareStateStore,
    private translocoService: TranslocoService,
  ) { }

  public ngOnInit(): void {
    if (this.initialGroupFetch) {
      this.initialGroupFetch()
        .pipe(
          take(1),
          map((page) => this.filterExcludedSubjectsFromPage(page)),
        )
        .subscribe((resp) => {
          this.groupedOptions[0].items = resp.content;
        });
    }

    this.filterTextObsvr = this.filterTextChanged
      .pipe(
        filter(() => (!this.loadingSearchResults)),
        debounceTime(600),
        tap((filterQuery) => {
          // Clear the search results if the user
          if (filterQuery.trim() === '') {
            this.filterValue = filterQuery;
            this.clearResults();
          }
        }),
        filter((filterQuery: string): boolean => filterQuery.length >= 2),
        distinctUntilChanged(),
        tap(() => {
          this.loadingSearchResults = true;
        }),
        switchMap((filterQuery: string): Observable<PageableUsers> => this.secondGroupFetch(filterQuery, 0)
          .pipe(
            map((page) => this.filterExcludedSubjectsFromPage(page)),
            tap(() => (this.loadingSearchResults = false)),
            filter(() => !this.queuedRequest),
            tap(() => (this.filterValue = filterQuery)),
            // Next tap is required to force p-dropdown to update after this.groupedOptions is updated.
            tap(() => {
              this.testClass += '1';
            }),
          )),
      );

    this.groupedOptions[0].label = this.translocoService.translate('sharePanel.select.recent');
    this.groupedOptions[1].label = this.translocoService.translate('sharePanel.select.searchResults');
  }

  private filterExcludedSubjectsFromPage(page: PageableUsers): PageableUsers {
    const filteredPage = page;
    filteredPage.content = this.filterExcludedSubjects(page.content);
    return filteredPage;
  }

  private filterExcludedSubjects(subjects: SubjectViewModel[]): SubjectViewModel[] {
    return subjects.filter((subject) => !this.excludeGroupFetchItems.find(({ id }) => id === subject.id));
  }

  public onFilter(event: { filter: string; }): void {
    if (this.loadingSearchResults) {
      this.queuedRequest = true;
    } else {
      this.queuedRequest = false;
    }
    this.filterTextChanged.next(event.filter);
  }

  public observeFilterchanges(): void {
    // create new reference, forcing Angular to recognize the change.
    this.groupedOptions = [...this.groupedOptions];
    this.modalState.showPanel(SharePanelOverlays.MULTI_SELECT_PANEL);
    this.filterTextObsvr
      .subscribe((results): void => {
        // Merge the results with the selected items, but exclude the selected items
        //  that are already in the search results. Otherwise, you end up with duplicates.
        this.pageState.isLast = results.isLast;
        this.pageState.page = results.number;
        this.groupedOptions[1].items = [
          ...results.content,
          ...this.selectedItems.filter((p) => !results.content.find((r: any) => r.id === p.id)),
        ];
        // create new reference, forcing Angular to recognize the change.
        this.groupedOptions = [...this.groupedOptions];
      });
  }

  public onSelect(event: { originalEvent: Event; value: SubjectViewModel[]; }): void {
    this.selectedOptions.emit(event.value);
    setTimeout(() => {
      this.multiSelectComponent.hide();
      this.groupedOptions[0].items = this.filterExcludedSubjects(this.groupedOptions[0].items);
    }, 0);
  }

  public cleanUpPanel() {
    this.clearResults();
    this.selectedItems = [];
    this.modalState.hidePanel(SharePanelOverlays.MULTI_SELECT_PANEL);
  }

  private clearResults(): void {
    this.groupedOptions[1].items = [];
  }

  public loadMore(filterValue: string): void {
    this.secondGroupFetch(filterValue, this.pageState.page + 1)
      .pipe(
        // Next line is required to force p-dropdown to update after this.groupedOptions is updated.
        tap(() => (this.filterValue = filterValue)),
      )
      .subscribe((results): void => {
        // Merge the results with the selected items, but exclude the selected items
        //  that are already in the search results. Otherwise, you end up with duplicates.
        this.pageState.isLast = results.isLast;
        this.pageState.page = results.number;
        this.groupedOptions[1].items.push(...results.content);

        this.filterValue = ' ';
        // Next line is required to force p-dropdown to update after this.groupedOptions is updated.
        // eslint-disable-next-line no-return-assign
        setTimeout(() => this.filterValue = filterValue);
      });
  }
}
